import { useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import MuiButton from '@mui/material/Button'
import { Link as RouterLink } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import WaterDropIcon from '@mui/icons-material/WaterDrop'
import GrassIcon from '@mui/icons-material/Grass'
import YardIcon from '@mui/icons-material/Yard'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import HeroSection from '../components/common/HeroSection'
import Container from '../components/common/Container'
import SectionIntro from '../components/common/SectionIntro'
import { cdnImage } from '../config/env'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

const focusAreas = [
    {
        icon: <GrassIcon sx={{ fontSize: 20, color: '#8A9A5B' }} />,
        label: 'Soil Regeneration',
        body: 'Restoring soil biology, organic matter, and structure through practices that work with natural systems rather than against them.',
    },
    {
        icon: <WaterDropIcon sx={{ fontSize: 20, color: '#8A9A5B' }} />,
        label: 'Water Resilience',
        body: 'Improving infiltration and water-holding capacity to build resilience against the droughts and extreme weather patterns intensified by climate change.',
    },
    {
        icon: <YardIcon sx={{ fontSize: 20, color: '#8A9A5B' }} />,
        label: 'Climate Adaptation',
        body: "Supporting farmers to reduce input dependency and sequester carbon while adapting to South Africa's increasingly volatile climate conditions.",
    },
]


const comparisonData = [
    { criterion: 'Soil health focus', regen: true, organic: true, conservation: false },
    { criterion: 'Input reduction', regen: true, organic: true, conservation: false },
    { criterion: 'Carbon sequestration', regen: true, organic: false, conservation: true },
    { criterion: 'Outcomes-based flexibility', regen: true, organic: false, conservation: false },
    { criterion: 'Certification available', regen: true, organic: true, conservation: false },
    { criterion: 'Biodiversity integration', regen: true, organic: true, conservation: false },
    { criterion: 'Reduced tillage', regen: true, organic: false, conservation: true },
    { criterion: 'Livestock integration', regen: true, organic: false, conservation: false },
]


function Check({ color = '#B87333' }: { color?: string }) {
    return <CheckCircleIcon sx={{ color, fontSize: 22 }} />
}
function Minus({ color = 'text.disabled' }: { color?: string }) {
    return <RemoveCircleOutlineIcon sx={{ color, fontSize: 20 }} />
}

export default function Home() {
    useEffect(() => {
        document.title = 'Future of Farm Food Library'
    }, [])

    return (
        <>
            {/* 1. Hero */}
            <HeroSection
                headline="Regenerative Agriculture and the Future of Farm Food in South Africa"
                subtext="A curated knowledge resource for farmers, food processors and retailers, researchers, consumers, investors and other ecosystem partners working towards and interested in a more resilient food system."
                primaryCta={{ label: 'Explore the Library', to: '/research' }}
                secondaryCta={{ label: 'Browse the Directory', to: '/directory' }}
                image={{
                    src: cdnImage('01-signature-hands-soil-sphere.png'),
                    alt: 'Hands holding a soil sphere',
                }}
                lightBg
                imageSquare
            />

            {/* 2. Intro — What is Regenerative Agriculture */}
            <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'background.default' }}>
                <Container>
                    <Grid container spacing={{ xs: 4, md: 6 }}>
                        {/* Text column */}
                        <Grid size={{ xs: 12, md: 7 }}>
                            <SectionIntro
                                overline="About regenerative agriculture"
                                heading="Farming that restores, not just sustains"
                            />
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
                                Regenerative agriculture is an outcomes-based approach to farming that restores and builds ecosystem health. Unlike extractive conventional systems, it prioritises soil biology, biodiversity, and water cycling — creating farms that become more productive, more resilient, and more valuable over time.
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                South Africa faces acute pressures: widespread soil degradation, increasing climate volatility, and deepening water scarcity. Regenerative practices offer a practical, evidence-based pathway for farmers to adapt to these realities while reducing input costs and improving long-term land productivity.
                            </Typography>
                        </Grid>
                        {/* Image column */}
                        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                component="img"
                                src={cdnImage('05-benefits-what-is-regen-roots-sphere.png')}
                                alt="Plant roots and soil ecosystem"
                                loading="eager"
                                sx={{
                                    width: { xs: 300, md: 420 },
                                    height: { xs: 300, md: 420 },
                                    objectFit: 'cover',
                                    display: 'block',
                                    flexShrink: 0,
                                }}
                            />
                        </Grid>
                        {/* Cards — full width below image and text */}
                        <Grid size={{ xs: 12 }}>
                            <Grid container spacing={2} alignItems="stretch">
                                {focusAreas.map((area) => (
                                    <Grid key={area.label} size={{ xs: 12, sm: 4 }}>
                                        <Box sx={{ position: 'relative', height: '100%', overflow: 'visible' }}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    left: -10,
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: '50%',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#8A9A5B',
                                                    border: '1px solid #8A9A5B',
                                                    boxShadow: 'none',
                                                    zIndex: 1,
                                                    '& svg': { fontSize: 18, color: '#ffffff' },
                                                }}
                                            >
                                                {area.icon}
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    border: '1px solid #8A9A5B',
                                                    backgroundColor: '#fff',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2.5,
                                                        pt: 1.25,
                                                        pb: 1.25,
                                                        backgroundColor: '#8A9A5B',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontWeight: 800,
                                                            fontSize: '1rem',
                                                            color: '#ffffff',
                                                            lineHeight: 1.2,
                                                            letterSpacing: '0.01em',
                                                            pl: 2,
                                                        }}
                                                    >
                                                        {area.label}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ px: 2.5, py: 1.75, backgroundColor: '#fff', flexGrow: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontSize: '0.9rem', color: 'text.secondary', lineHeight: 1.65 }}
                                                    >
                                                        {area.body}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                            <MuiButton
                                component={RouterLink}
                                to="/benefits"
                                variant="contained"
                                color="primary"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                                sx={{ mt: 4 }}
                            >
                                Why It Matters
                            </MuiButton>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 3. Comparison */}
            <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'rgba(138, 154, 91, 0.1)' }}>
                <Container>
                    <SectionIntro
                        overline="Comparison"
                        heading="Regenerative vs Organic vs Conservation Agriculture"
                        body="Each farming practice has merit, and there is significant overlap. Understanding the differences helps farmers, food processors, retailers, investors and customers make informed choices."
                    />
                    {/* Desktop table */}
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            border: '1px solid #8A9A5B',
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0 18px 34px rgba(27, 31, 29, 0.10)',
                        }}
                    >
                        <Table
                            sx={{
                                '& .MuiTableCell-root': {
                                    borderBottom: '1px solid rgba(138, 154, 91, 0.35)',
                                    fontSize: '1rem',
                                    color: 'text.primary',
                                },
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: '1rem',
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#0B2027' }}>
                                    <TableCell>Criterion</TableCell>
                                    <TableCell align="center">Regenerative</TableCell>
                                    <TableCell align="center">Organic</TableCell>
                                    <TableCell align="center">Conservation</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {comparisonData.map((row, i) => (
                                    <TableRow
                                        key={row.criterion}
                                        sx={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#8A9A5B' }}
                                    >
                                        <TableCell sx={{ fontWeight: 600 }}>{row.criterion}</TableCell>
                                        <TableCell align="center">{row.regen ? <Check color={i % 2 === 0 ? 'secondary.main' : 'secondary.dark'} /> : <Minus  color="dark" />}</TableCell>
                                        <TableCell align="center">{row.organic ? <Check  color={i % 2 === 0 ? 'secondary.main' : 'secondary.dark'}  /> : <Minus color="dark"/>}</TableCell>
                                        <TableCell align="center">{row.conservation ? <Check  color={i % 2 === 0 ? 'secondary.main' : 'secondary.dark'} /> : <Minus  color="dark"  />}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Mobile cards */}
                    <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                        {[
                            { name: 'Regenerative', tagline: 'Outcomes-based, restorative', featured: true, key: 'regen' as const },
                            { name: 'Organic', tagline: 'Input-based, certification-led', featured: false, key: 'organic' as const },
                            { name: 'Conservation', tagline: 'Process-focused, soil protective', featured: false, key: 'conservation' as const },
                        ].map((approach) => (
                            <Paper
                                key={approach.name}
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: approach.featured ? '1.5px solid #8A9A5B' : '1px solid rgba(27, 31, 29, 0.12)',
                                    boxShadow: approach.featured ? '0 8px 24px rgba(138, 154, 91, 0.25)' : '0 4px 12px rgba(27, 31, 29, 0.06)',
                                }}
                            >
                                <Box sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    backgroundColor: approach.featured ? '#0B2027' : 'rgba(27, 31, 29, 0.04)',
                                    borderBottom: '1px solid rgba(27, 31, 29, 0.08)',
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', color: approach.featured ? '#fff' : 'text.primary' }}>
                                        {approach.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: approach.featured ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
                                        {approach.tagline}
                                    </Typography>
                                </Box>
                                <Box>
                                    {comparisonData.map((row, i) => (
                                        <Box
                                            key={row.criterion}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                px: 2.5,
                                                py: 1.25,
                                                backgroundColor: i % 2 === 0 ? '#ffffff' : '#8A9A5B',
                                                borderBottom: i < comparisonData.length - 1 ? '1px solid rgba(138, 154, 91, 0.3)' : 'none',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: '0.95rem', fontWeight: 600, color: i % 2 === 0 ? 'text.primary' : '#0B2027' }}>
                                                {row.criterion}
                                            </Typography>
                                            {row[approach.key] ? <Check /> : <Minus />}
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                </Container>
            </Box>

            {/* 4. Research Library */}
            <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'background.default' }}>
                <Container>
                    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                        <Grid size={{ xs: 12, md: 7 }}>
                            <SectionIntro
                                overline="Research Library"
                                heading="Evidence for the transition"
                            />
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
                                The primary intention of this site is to provide a curated library which brings together academic research, policy analysis, field reports, and ecosystem studies focused on regenerative agriculture in Southern Africa and around the world. This library contains over 200 reports/studies that provide the relevant evidence for adopting regenerative practices.   The library can be filtered by theme or simply by asking a question in the chatbot function.
                            </Typography>
                            <MuiButton
                                component={RouterLink}
                                to="/research"
                                variant="contained"
                                color="primary"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                                sx={{ mt: 1 }}
                            >
                                Explore the Library
                            </MuiButton>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                component="img"
                                src={cdnImage('03-research-library-books-sphere.png')}
                                alt="Bookshelf representing the research library"
                                loading="lazy"
                                sx={{
                                    width: { xs: 300, md: 420 },
                                    height: { xs: 300, md: 420 },
                                    objectFit: 'cover',
                                    display: 'block',
                                    flexShrink: 0,
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 5. Ecosystem Directory */}
            <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'rgba(138, 154, 91, 0.1)' }}>
                <Container>
                    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center', order: { xs: 2, md: 1 } }}>
                            <Box
                                component="img"
                                src={cdnImage('04-contact-directory-field-shovel-sphere_2.png')}
                                alt="Field path with a shovel representing the ecosystem directory"
                                loading="lazy"
                                sx={{
                                    width: { xs: 300, md: 420 },
                                    height: { xs: 300, md: 420 },
                                    objectFit: 'cover',
                                    display: 'block',
                                    flexShrink: 0,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 1, md: 2 } }}>
                            <SectionIntro
                                overline="Ecosystem Directory"
                                heading="Connect and collaborate"
                            />
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
                                The regenerative agriculture ecosystem in South Africa encompasses farmers, researchers, funders, NGOs, government bodies, and private sector partners. To enable the ecosystem, the Directory maps this landscape so that collaboration, learning, and investment can flow more effectively. It is designed to evolve by allowing new stakeholders to be added to the Directory.
                            </Typography>
                            <MuiButton
                                component={RouterLink}
                                to="/directory"
                                variant="contained"
                                color="primary"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                            >
                                Browse the Directory
                            </MuiButton>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    )
}
